import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { crmData } from '../data/crmData';

export const ClientHealthOverview = () => {
  const [filter, setFilter] = useState('all'); // 'all', 'risk', 'stable'

  // Beregn portefølje-statistikk
  const stats = useMemo(() => {
    const total = crmData.clients.length;
    const avgHealth = Math.round(crmData.clients.reduce((acc, c) => acc + c.healthScore, 0) / total);
    const atRisk = crmData.clients.filter(c => c.healthScore < 50).length;
    const activeProjects = crmData.projects.filter(p => p.status === 'Pågår').length;
    
    return { total, avgHealth, atRisk, activeProjects };
  }, []);

  const filteredClients = useMemo(() => {
    if (filter === 'risk') return crmData.clients.filter(c => c.healthScore < 50);
    if (filter === 'stable') return crmData.clients.filter(c => c.healthScore >= 70);
    return crmData.clients;
  }, [filter]);

  // Helsefordeling (Pie Chart)
  const distributionOption = {
    tooltip: { trigger: 'item' },
    series: [
      {
        name: 'Helsestatus',
        type: 'pie',
        radius: ['60%', '80%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: '12', fontWeight: 'bold' } },
        data: [
          { value: stats.atRisk, name: 'Kritisk (<50)', itemStyle: { color: '#f43f5e' } },
          { value: stats.total - stats.atRisk - crmData.clients.filter(c => c.healthScore >= 75).length, name: 'Moderat (50-75)', itemStyle: { color: '#fbbf24' } },
          { value: crmData.clients.filter(c => c.healthScore >= 75).length, name: 'Godt (>75)', itemStyle: { color: '#10b981' } }
        ]
      }
    ]
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Client Health Dashboard</h1>
          <p className="text-slate-500 mt-1">Sanntidsoversikt over porteføljens psykososiale tilstand.</p>
        </div>
        <div className="flex gap-2 bg-slate-100 p-1 rounded-xl">
          <button 
            onClick={() => setFilter('all')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${filter === 'all' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Alle
          </button>
          <button 
            onClick={() => setFilter('risk')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${filter === 'risk' ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-500 hover:text-rose-600'}`}
          >
            Høy Risiko
          </button>
        </div>
      </div>

      {/* Top Level Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Portefølje', val: stats.total, icon: 'Users', color: 'indigo' },
          { label: 'Snitt Helse-score', val: `${stats.avgHealth}%`, icon: 'Activity', color: 'emerald' },
          { label: 'Kunder i Risikosonen', val: stats.atRisk, icon: 'AlertTriangle', color: 'rose' },
          { label: 'Aktive Intervensjoner', val: stats.activeProjects, icon: 'Zap', color: 'amber' },
        ].map((stat, i) => (
          <Card key={i}>
            <CardContent className="p-5 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl bg-${stat.color}-50 text-${stat.color}-600 flex items-center justify-center`}>
                <SafeIcon name={stat.icon} className="text-xl" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
                <p className="text-2xl font-black text-slate-900">{stat.val}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Grid */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Kundeoversikt</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredClients.map((client) => (
              <Card key={client.id} hover={true} className="group">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-slate-900">{client.name}</h3>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">{client.industry}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-lg text-[10px] font-black ${
                      client.healthScore < 50 ? 'bg-rose-50 text-rose-600' : 
                      client.healthScore < 75 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
                    }`}>
                      {client.healthScore}% HELSE
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-[10px] items-center">
                      <span className="text-slate-500 font-bold">AKTIVE PROSJEKTER</span>
                      <span className="text-slate-900 font-black">{client.projectsCount}</span>
                    </div>
                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          client.healthScore < 50 ? 'bg-rose-500' : 
                          client.healthScore < 75 ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${client.healthScore}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 mt-5 pt-4 border-t border-slate-50">
                    <Button variant="secondary" size="sm" className="flex-1 text-[10px]">Analysere</Button>
                    <Button variant="ghost" size="sm" className="text-slate-400"><SafeIcon name="MoreHorizontal" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Portfolio Analysis */}
        <div className="space-y-6">
          <Card>
            <CardHeader title="Helsefordeling" subtitle="Hele porteføljen" />
            <CardContent>
              <div className="h-64">
                <ReactECharts option={distributionOption} style={{ height: '100%' }} />
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-rose-500"/> Kritisk</span>
                  <span className="font-bold">{stats.atRisk} kunder</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-amber-400"/> Moderat</span>
                  <span className="font-bold">{stats.total - stats.atRisk - crmData.clients.filter(c => c.healthScore >= 75).length} kunder</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500"/> God</span>
                  <span className="font-bold">{crmData.clients.filter(c => c.healthScore >= 75).length} kunder</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-indigo-900 border-none text-white">
            <CardHeader title="Konsulent-tips" icon={<SafeIcon name="Zap" className="text-indigo-400" />} />
            <CardContent className="space-y-4">
              <p className="text-xs text-indigo-100 leading-relaxed">
                <span className="font-bold text-white">Nordic Retail</span> har hatt et fall på 12% i psykologisk trygghet denne uken. Anbefaler å kontakte Linda Berg for en oppfølgingssamtale.
              </p>
              <Button variant="primary" className="w-full bg-indigo-500 hover:bg-indigo-400 border-none text-[11px]">
                Vis alle kritiske endringer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};